import { useContext, useEffect, useState } from 'react';

import { Text } from '@gympass/yoga';
import { ArrowRight, AlertTriangle } from '@gympass/yoga-icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatDateHour } from 'utils';

import { SpecialistContext } from 'context';
import { getSpecialistType } from 'context/SpecialistContext/SpecialistContext';
import { SpecialistRole, SpecialistType } from 'services/specialist';
import { RecordQuestion } from 'services/user';
import { createPatientRecords, getPatientRecords } from 'services/user/user';

import { RecordEntriesErrorState, Tooltip } from 'components';
import DrawerPatientRecord from 'components/DrawerPatientRecord/DrawerPatientRecord';

import * as S from './style';

import { PatientRecordsProps } from './types';

const PatientRecords: React.FC<PatientRecordsProps> = ({ userCognitoId }) => {
  const intl = useIntl();
  const { specialistInfo } = useContext(SpecialistContext);
  const [openPatienRecordDrawer, setOpenPatienRecordDrawer] = useState(false);
  const { formatMessage } = useIntl();
  const specialistType = getSpecialistType(specialistInfo?.groups);
  const [selectedQuestion, setSelectedQuestion] = useState(
    {} as RecordQuestion,
  );

  const loadingSkeletonLength = specialistType === SpecialistType.TC ? 5 : 2;

  const [patientRecordNotFound, setPatientRecordNotFound] = useState(false);

  const {
    isError: isPostRecordError,
    isSuccess: isPostRecordSuccess,
    isLoading: isLoadingPostRecord,
    mutate: createPatientRecordsMutation,
  } = useMutation({
    mutationFn: createPatientRecords,
    onSuccess: () => setPatientRecordNotFound(false),
  });

  const {
    data: patientRecords,
    isError: isGetRecordError,
    isLoading: isLoadingGetRecord,
    refetch: refetchGetPatientRecords,
  } = useQuery({
    enabled: !!userCognitoId,
    queryKey: [userCognitoId, 'patientRecords'],
    queryFn: () => getPatientRecords(userCognitoId),
    onError: (e: AxiosError) => {
      if (e.response?.status === 404) {
        setPatientRecordNotFound(true);
        createPatientRecordsMutation(userCognitoId);
      }
    },
  });

  useEffect(() => {
    if (isGetRecordError && isPostRecordSuccess) {
      refetchGetPatientRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetRecordError, isPostRecordSuccess]);

  const handleSuccessOnPatientRecordCreation = () => {
    setOpenPatienRecordDrawer(false);
    setSelectedQuestion({} as RecordQuestion);
    refetchGetPatientRecords();
  };

  const handleErrorOnPatientRecordCreation = () => {
    setOpenPatienRecordDrawer(false);
    setSelectedQuestion({} as RecordQuestion);
    refetchGetPatientRecords();
  };

  const renderDrawers = () => {
    if (userCognitoId && patientRecords && selectedQuestion.title_key) {
      return (
        <>
          <DrawerPatientRecord
            onCloseDrawer={() => setOpenPatienRecordDrawer(false)}
            openDrawer={openPatienRecordDrawer}
            userCognitoId={userCognitoId}
            patientRecordId={patientRecords.id}
            questionId={selectedQuestion.id}
            titleKey={selectedQuestion.title_key}
            labelKey={selectedQuestion.field_description_key}
            tooltipDescription={selectedQuestion.tooltip_description_key}
            handleCreationSuccess={handleSuccessOnPatientRecordCreation}
            handleCreationError={handleErrorOnPatientRecordCreation}
          />
        </>
      );
    }

    return <></>;
  };

  function handleOpenQuestionHistory(question: RecordQuestion) {
    setSelectedQuestion(question);
    setOpenPatienRecordDrawer(true);
  }

  return (
    <>
      {renderDrawers()}
      <S.PatientRecords data-testid="patientRecordsSection">
        <Text.H5 data-testid="patientRecordsTitle">
          <FormattedMessage id="PSYCHOLOGICALRECORD.SECTIONTITLE.RECORD" />
        </Text.H5>
        <S.SubTitle data-testid="patientRecordsSubTitle">
          <FormattedMessage
            id="PSYCHOLOGICALRECORD.SECTIONSUBTITLE.FILLINGINSTRUCTION"
            values={{
              b: (text: string) => (
                <S.SubTitleHighlight>{text}</S.SubTitleHighlight>
              ),
            }}
          />
        </S.SubTitle>
        {(isLoadingGetRecord || isLoadingPostRecord) && (
          <div data-testid="patientRecordsLoading">
            {Array.from({ length: loadingSkeletonLength }, (_, index) => (
              <S.RecordTopic key={index}>
                <S.LoadingInfo type="text" variant="body1" />
              </S.RecordTopic>
            ))}
          </div>
        )}
        {(isPostRecordError ||
          (isGetRecordError && !patientRecordNotFound)) && (
          <RecordEntriesErrorState />
        )}
        {patientRecords?.questions && (
          <div data-testid="patientRecordQuestions">
            {patientRecords?.questions.map(question => (
              <S.RecordTopic
                key={question.id}
                data-testid="patientRecordQuestion"
                onClick={() => handleOpenQuestionHistory(question)}
              >
                <S.RecordTopicContainer>
                  <S.RecordTopicHeader>
                    <S.RecordTopicTitle
                      data-testid="patientRecordQuestionTitle"
                      hasContent={!!question.content}
                    >
                      <FormattedMessage id={question.title_key} />
                    </S.RecordTopicTitle>
                    {question.tooltip_description_key && (
                      <Tooltip
                        position="top-center"
                        description={question.tooltip_description_key}
                      >
                        <S.MoreInfoIconContainer>
                          <S.MoreInfoIcon />
                        </S.MoreInfoIconContainer>
                      </Tooltip>
                    )}
                    {question.date && (
                      <S.LastUpdateInfo data-testid="patientRecordQuestionUpdateInfo">
                        <FormattedMessage id="PSYCHOLOGICALRECORD.FIELDINFO.LASTUPDATE" />
                        {formatDateHour(question.date, { locale: intl.locale })}
                      </S.LastUpdateInfo>
                    )}
                    {question.allowed_roles.includes(SpecialistRole.TC) &&
                      specialistType !== SpecialistType.TC && (
                        <Tooltip
                          width="auto"
                          position="top-center"
                          description={formatMessage({
                            id: 'PSYCHOLOGICALRECORD.TOOLTIPDESCRIPTION.SHAREDLABEL',
                          })}
                        >
                          <S.ShareableTag
                            mr="small"
                            icon={AlertTriangle}
                            data-testid="patientRecordShareableRecordTopic"
                          >
                            <FormattedMessage id="PSYCHOLOGICALRECORD.LABEL.SHARED" />
                          </S.ShareableTag>
                        </Tooltip>
                      )}
                  </S.RecordTopicHeader>
                  {question.content && (
                    <S.RecordTopicContent data-testid="patientRecordContent">
                      <FormattedMessage id={question.content} />
                    </S.RecordTopicContent>
                  )}
                </S.RecordTopicContainer>
                <S.ViewMoreButton
                  inverted
                  icon={ArrowRight}
                  data-testid="patientRecordViewMoreButton"
                  onClick={() => handleOpenQuestionHistory(question)}
                />
              </S.RecordTopic>
            ))}
          </div>
        )}
      </S.PatientRecords>
    </>
  );
};

export default PatientRecords;
